import React, { useState, useEffect } from "react";
import { Grid, Box, Button, Typography, CircularProgress, Checkbox, FormControlLabel } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import InboundHeaderNoPO from "../../molecules/Inventory/InboundHeaderNoPO/index.tsx";
import InboundItemsNoPO from "../../molecules/Inventory/InboundItemsNoPO/index.tsx";
import { useCompany } from "../../contexts/CompanyContext";
import { createInbound, getInboundById, updateInbound } from "../../api/inbounds/index.ts";
import { useSnack } from "../../services/SnakbarProvider.js";
import dayjs from "dayjs";

interface InboundFormNoPOProps {
  onSubmit: () => void;
  id?: number;
}

const validationSchema = Yup.object().shape({
  inbound: Yup.object().shape({
    companyId: Yup.number().required("La empresa es requerida"),
    description: Yup.string(),
    warehouseId: Yup.number().required("El almacén es requerido"),
    isNational: Yup.boolean(),
    inboundDate: Yup.date().nullable(),
  }),
  inboundItems: Yup.array().of(
    Yup.object().shape({
      description: Yup.string(),
      quantityDelivered: Yup.number()
        .required("La cantidad es requerida")
        .min(1, "La cantidad debe ser mayor que 0"),
      weight: Yup.number()
        .required("El peso es requerido")
        .min(1, "El peso debe ser mayor que 0"),
      productId: Yup.number().nullable(),
      productCategoryId: Yup.number().nullable(),
    })
  ),
});

const InboundFormNoPO: React.FC<InboundFormNoPOProps> = ({ onSubmit, id }) => {
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<any>({
    inbound: {
      companyId: null,
      description: '',
      warehouseId: null,
      isNational: false,
      inboundDate: null,
    },
    inboundItems: [],
  });
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getInboundById(id);
      const data = response.data;
      setFormData({
        inbound: {
          companyId: data.companyId,
          description: data.description,
          warehouseId: data.warehouseId,
          isNational: data.isNational || false,
          inboundDate: data.inboundDate ? dayjs(data.inboundDate) : null,
        },
        inboundItems: data.inboundItems?.map((item: any) => ({
          description: item.description,
          deliveryNo: item.deliveryNo,
          quantityDelivered: item.quantityDelivered,
          weight: item.weight,
          productId: item.productId,
          productCategoryId: item.productCategoryId,
        })) || [],
      });
    } catch (error) {
      console.log(error);
      openSnack("Error al cargar la orden de ingreso", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formMode === "edit" && id) {
      fetchData();
    } else {
      setFormData({
        inbound: {
          companyId: selectedCompany.id,
          description: '',
          warehouseId: null,
          isNational: false,
          inboundDate: null,
        },
        inboundItems: [],
      });
      setLoading(false);
    }
  }, [id, selectedCompany.id, formMode]);

  const createPayload = (values: any) => {
    const formatDate = (date: any) => (date ? dayjs(date).format("YYYY-MM-DD") : null);
    return {
      inbound: {
        ...values.inbound,
        companyId: selectedCompany.id,
        inboundDate: formatDate(values.inbound.inboundDate),
      },
      inboundItems: values.inboundItems,
    };
  };

  const handleSubmit = async (values: any) => {
    const payload = createPayload(values);
    try {
      if (id) {
        await updateInbound(id, payload);
        openSnack("Orden de ingreso actualizada correctamente", "success");
      } else {
        await createInbound(payload);
        openSnack("Orden de ingreso creada correctamente", "success");
      }
      onSubmit();
    } catch (error) {
      openSnack("Error al guardar la orden de ingreso", "error");
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Formik
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, setFieldValue, errors }) => (
        <Form>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <InboundHeaderNoPO
                  companyId={selectedCompany.id}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <InboundItemsNoPO
                  formMode={formMode}
                  companyId={selectedCompany.id}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      console.log(errors);
                    }}
                  >
                    {formMode === "edit" ? "Actualizar" : "Guardar"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default InboundFormNoPO;
